import { useCallback, useEffect, useState } from "react";
import { Link } from "wouter";
import Sidebar from "@/components/Sidebar";
import { apiJson } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type AuditItem = {
  id: string;
  user_id: number | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  old_value: string | null;
  new_value: string | null;
  ip: string | null;
  user_agent: string | null;
  result: string;
  created_at: string;
};

export default function AdminAuditPage() {
  const [items, setItems] = useState<AuditItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [userId, setUserId] = useState("");
  const [action, setAction] = useState("");
  const [entityType, setEntityType] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const q = new URLSearchParams();
    q.set("page", String(page));
    q.set("pageSize", "40");
    if (userId.trim()) q.set("userId", userId.trim());
    if (action.trim()) q.set("action", action.trim());
    if (entityType.trim()) q.set("entityType", entityType.trim());
    if (from.trim()) q.set("from", from.trim());
    if (to.trim()) q.set("to", to.trim());
    const data = await apiJson<{ items: AuditItem[]; total: number }>(`/api/admin/audit?${q}`);
    setItems(data.items);
    setTotal(data.total);
  }, [page, userId, action, entityType, from, to]);

  useEffect(() => {
    setPage(1);
  }, [userId, action, entityType, from, to]);

  useEffect(() => {
    load().catch((e) => setError(String(e)));
  }, [load]);

  return (
    <div className="min-h-screen flex" style={{ background: "var(--background)" }}>
      <Sidebar />
      <main className="max-w-[1400px] flex-1 overflow-x-auto p-8 transition-[margin-left] duration-200 ease-out [margin-left:var(--app-sidebar-width)]">
        <div className="mb-6 flex items-center justify-between gap-4">
          <h1 className="text-2xl font-bold">Журнал аудита</h1>
          <Button variant="outline" asChild>
            <Link href="/">На главную</Link>
          </Button>
        </div>

        {error ? <p className="text-red-600 text-sm mb-4">{error}</p> : null}

        <div className="rounded-lg border border-[var(--sidebar-border)] p-4 mb-6 flex flex-wrap gap-4 items-end">
          <div className="space-y-1">
            <Label>userId</Label>
            <Input value={userId} onChange={(e) => setUserId(e.target.value)} className="w-24" />
          </div>
          <div className="space-y-1">
            <Label>action</Label>
            <Input value={action} onChange={(e) => setAction(e.target.value)} className="w-40" />
          </div>
          <div className="space-y-1">
            <Label>entityType</Label>
            <Input value={entityType} onChange={(e) => setEntityType(e.target.value)} className="w-32" />
          </div>
          <div className="space-y-1">
            <Label>from (ISO)</Label>
            <Input value={from} onChange={(e) => setFrom(e.target.value)} className="w-48" placeholder="2026-01-01" />
          </div>
          <div className="space-y-1">
            <Label>to (ISO)</Label>
            <Input value={to} onChange={(e) => setTo(e.target.value)} className="w-48" />
          </div>
          <Button type="button" variant="secondary" onClick={() => setPage(1)}>
            Сбросить страницу
          </Button>
        </div>

        <p className="text-sm text-muted-foreground mb-2">Всего записей: {total}</p>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Время</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Действие</TableHead>
              <TableHead>Сущность</TableHead>
              <TableHead>Результат</TableHead>
              <TableHead>IP</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="whitespace-nowrap text-xs">{row.created_at}</TableCell>
                <TableCell>{row.user_id ?? "—"}</TableCell>
                <TableCell className="text-xs">{row.action}</TableCell>
                <TableCell className="text-xs">
                  {row.entity_type ?? "—"} {row.entity_id ? `/${row.entity_id}` : ""}
                </TableCell>
                <TableCell>{row.result}</TableCell>
                <TableCell className="text-xs">{row.ip ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="mt-4 flex gap-2">
          <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Назад
          </Button>
          <Button variant="outline" disabled={page * 40 >= total} onClick={() => setPage((p) => p + 1)}>
            Вперёд
          </Button>
        </div>
      </main>
    </div>
  );
}
